// Display helpers for the admin Batches page: how a batch status looks, and how to word live PDF progress.

export const BATCH_STATUS_TONES = {
  SUCCESS: 'success',
  PROGRESS: 'progress',
  ERROR: 'error',
  NEUTRAL: 'neutral',
};

// Statuses the batch has reached once its PDF exists and it moved on to printing / tracking
const SUCCESS_STATUSES = [
  'pdf generated',
  'submitted to woo',
  'ready for accutrace',
  'accutrace job ids retrieved',
  'completed',
];

const NEUTRAL_STATUSES = ['test batch', 'accuzip processing not required'];

const ERROR_PATTERN = /fail|error|bad csv|cancel/;

// The status strings come from the server (olc-server and olc-batching-server each write some), so anything
// not listed above is classified by its wording instead of silently falling back to grey.
const PROGRESS_PATTERN = /pdf|chunk|render|combin|fetch|upload|merg|cleanup|processing|progress|ready for|pending/;

export const getBatchStatusTone = (status) => {
  const value = String(status || '').trim().toLowerCase();

  if (!value) return BATCH_STATUS_TONES.NEUTRAL;
  if (ERROR_PATTERN.test(value)) return BATCH_STATUS_TONES.ERROR;
  if (SUCCESS_STATUSES.includes(value)) return BATCH_STATUS_TONES.SUCCESS;
  if (NEUTRAL_STATUSES.includes(value)) return BATCH_STATUS_TONES.NEUTRAL;
  if (PROGRESS_PATTERN.test(value)) return BATCH_STATUS_TONES.PROGRESS;

  return BATCH_STATUS_TONES.NEUTRAL;
};

export const BATCH_PAUSED_STATE = 'SUSPEND';

const PROGRESS_STEPS = {
  batch_creation: { label: 'Preparing', unit: 'rows' },
  render_chunks: { label: 'Rendering', unit: 'chunks' },
  combine_fetching: { label: 'Fetching PDFs', unit: 'chunks' },
  combine_merging: { label: 'Merging', unit: 'chunks' },
  combine_uploading: { label: 'Uploading', unit: 'files' },
  combine_cleaning: { label: 'Cleaning up' },
};

const formatCount = (value) => Number(value).toLocaleString('en-US');

export const getBatchProgressPercent = (progress) => {
  const value = Number(progress?.overallPercent);
  return Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0;
};

/**
 * One line under the bar: what the step is doing, what it has counted so far (when it counts something) and how far
 * along the step itself is, e.g. "Rendering · 3/10 chunks · 30% · 1 failed".
 */
export const getBatchProgressCaption = (progress) => {
  if (!progress) return '';

  const step = PROGRESS_STEPS[progress.step];
  const parts = [step?.label || 'Processing'];

  if (step?.unit && progress.current !== null && progress.current !== undefined && Number(progress.total) > 0) {
    parts.push(`${formatCount(progress.current)}/${formatCount(progress.total)} ${step.unit}`);
  }

  parts.push(`${Math.round(Number(progress.stepPercent) || 0)}%`);

  if (Number(progress.failed) > 0) {
    parts.push(`${formatCount(progress.failed)} failed`);
  }

  return parts.join(' \u00b7 ');
};

/**
 * Where each status of the PDF pipeline sits on the overall bar. The strings are the ones olc-batching-server writes
 * (`Ready for PDF` is set by olc-server). `step` is the index of the pipeline step the status belongs to, `floor` the
 * overall percent the bar is at least at, and `active` whether the step is running (its progress event has counts to
 * show) or the batch is waiting for the next step to start.
 */
const PIPELINE = {
  'ready for pdf': { step: -1, floor: 0, label: 'Queued', active: false },
  'chunks processing': { step: 0, floor: 0, label: 'Preparing', active: true },
  'chunks completed': { step: 0, floor: 20, label: 'Waiting to render', active: false },
  'rendering started': { step: 1, floor: 20, label: 'Rendering', active: true },
  'rendering completed': { step: 1, floor: 80, label: 'Waiting to combine', active: false },
  'fetching chunks': { step: 2, floor: 80, label: 'Fetching PDFs', active: true },
  'combining pdfs': { step: 3, floor: 88, label: 'Merging', active: true },
  'uploading files': { step: 4, floor: 95, label: 'Uploading', active: true },
  'cleanup leftover': { step: 5, floor: 99, label: 'Cleaning up', active: true },
};

// Index of each step in the progress events (the same order as PIPELINE)
const PROGRESS_STEP_INDEX = {
  batch_creation: 0,
  render_chunks: 1,
  combine_fetching: 2,
  combine_merging: 3,
  combine_uploading: 4,
  combine_cleaning: 5,
};

/**
 * What the bar under the status badge shows, or null for a batch that is not on its way to a PDF.
 *
 * The status is the source of truth: the bar follows it, so it moves on every status change even when no progress event
 * arrives. A progress snapshot only refines it while it belongs to the current step: one from an earlier step ("Preparing
 * 25/25 rows" while the batch is already rendering) is stale and ignored.
 *
 * @param {{ batchStatus?: string, progress?: object|null }} row
 * @returns {{ percent: number, caption: string, updatedAt: string|null }|null}
 */
export const getBatchProgressView = (row) => {
  const stage = PIPELINE[String(row?.batchStatus || '').trim().toLowerCase()];
  if (!stage) return null;

  const { progress } = row;
  const progressStep = PROGRESS_STEP_INDEX[progress?.step];
  const belongsHere = progress && progressStep !== undefined && progressStep >= stage.step;

  return {
    percent: Math.max(stage.floor, belongsHere ? getBatchProgressPercent(progress) : 0),
    caption: stage.active && belongsHere && progressStep === stage.step ? getBatchProgressCaption(progress) : stage.label,
    updatedAt: belongsHere ? progress.updatedAt ?? null : null,
  };
};
